import React, { useState, useRef } from 'react';
import { View, Text, ScrollView, StyleSheet, Dimensions, ImageBackground, Image, TouchableOpacity, Platform } from 'react-native';
import HeaderBar, { useScrollHide } from '../Widgets/HeaderBar';
import { HEADER_PADDING } from '../constants';

const { width } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width <= 1440;

// Video URL variable 
const MYTASSEL_VIDEO_URL =
  'https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/videos%2FMyTassel_Video.mp4?alt=media&token=78a2502b-c5dd-448e-9c49-573b2a0a1f33';

const WhyMyTassel = () => {
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const videoRef = useRef(null);
  const { headerTranslateY, onScroll } = useScrollHide('scrollView');

  return (
    <View style={styles.mainContainer}>
      <ImageBackground source={require('../assets/Landing Background.png')} style={styles.backgroundImage}>
        <View style={styles.backgroundTint}/>
        <HeaderBar headerTranslateY={headerTranslateY} />
        <ScrollView
          style={styles.body}
          onScroll={onScroll}
          scrollEventThrottle={16}
          contentContainerStyle={{ flexGrow: 1, paddingTop: isMobile ? HEADER_PADDING.mobile : isTablet ? HEADER_PADDING.tablet : HEADER_PADDING.desktop }}
        >
        <View style={styles.content}>
          <Text style={styles.title}>Why Choose MyTassel?</Text>

          {/* Video Section */}
          <View style={styles.videoWrapper}>
            {Platform.OS === 'web' ? (
              videoError ? (
                // Fallback when video fails to load (expired token, CORS, etc.)
                <TouchableOpacity
                  style={styles.videoPlaceholder}
                  activeOpacity={0.85}
                  onPress={() => window.open(MYTASSEL_VIDEO_URL, '_blank')}
                >
                  <Text style={styles.videoPlaceholderText}>▶ Watch Video</Text>
                  <Text style={[styles.videoPlaceholderText, { fontSize: 13, marginTop: 6, opacity: 0.7 }]}>Tap to open in new tab</Text>
                </TouchableOpacity>
              ) : (
                <View style={{ width: '100%', position: 'relative' }}>
                  {videoLoading && (
                    <View style={styles.videoPlaceholder}>
                      <Text style={styles.videoPlaceholderText}>⏳ Loading video…</Text>
                    </View>
                  )}
                  <video
                    ref={videoRef}
                    src={MYTASSEL_VIDEO_URL}
                    controls
                    autoPlay
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={() => {
                      // Seek to 3s so that frame shows as the thumbnail, not a black screen.
                      if (videoRef.current) videoRef.current.currentTime = 3;
                      setVideoLoading(false);
                    }}
                    onCanPlay={() => setVideoLoading(false)}
                    onError={() => { setVideoLoading(false); setVideoError(true); }}
                    style={{
                      width: '100%',
                      maxWidth: 900,
                      maxHeight: 500,
                      borderRadius: 16,
                      backgroundColor: '#000',
                      display: videoLoading ? 'none' : 'block',
                    }}
                  />
                </View>
              )
            ) : (
              <TouchableOpacity
                activeOpacity={0.9}
                onPress={() => null}
                style={styles.videoPlaceholder}
              >
                <Text style={styles.videoPlaceholderText}>▶ Watch our video</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* View Sample Page Button */}
          <View style={{ alignItems: 'center', marginBottom: 32 }}>
            <TouchableOpacity
              style={styles.sampleButton}
              activeOpacity={0.85}
              onPress={() => Platform.OS === 'web' ? window.open('https://mytassel.com/events/G06785', '_blank') : null}
            >
              <Text style={styles.sampleButtonText}>View Sample Page</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.reasonsGrid}>
            <View style={styles.reasonCard}>
              <Text style={styles.checkmark}>✅</Text>
              <View style={styles.reasonContent}>
                <Text style={styles.reasonText}>Easy to Use</Text>
                <Text style={styles.description}>Create your graduation registry and announcements in minutes.</Text>
              </View>
            </View>
            
            <View style={styles.reasonCard}>
              <Text style={styles.checkmark}>✅</Text>
              <View style={styles.reasonContent}>
                <Text style={styles.reasonText}>Free to Create</Text>
                <Text style={styles.description}>All features, including the registry, virtual yearbook, and announcements, are completely free to set up.</Text>
              </View>
            </View>
            
            <View style={styles.reasonCard}>
              <Text style={styles.checkmark}>✅</Text>
              <View style={styles.reasonContent}>
                <Text style={styles.reasonText}>Keeps Everyone Connected</Text>
                <Text style={styles.description}>Send digital announcements and keep family and friends informed about your big day.</Text>
              </View>
            </View>
            
            <View style={styles.reasonCard}>
              <Text style={styles.checkmark}>✅</Text>
              <View style={styles.reasonContent}>
                <Text style={styles.reasonText}>Modern and Convenient</Text>
                <Text style={styles.description}>No more paper invitations or outdated processes—everything is digital and accessible.</Text>
              </View>
            </View>
            
            <View style={styles.reasonCard}>
              <Text style={styles.checkmark}>✅</Text>
              <View style={styles.reasonContent}>
                <Text style={styles.reasonText}>Personalized Features</Text>
                <Text style={styles.description}>Set goals, track gifts, and create a unique experience tailored to your graduation.</Text>
              </View>
            </View>
            
            <View style={styles.reasonCard}>
              <Text style={styles.checkmark}>✅</Text>
              <View style={styles.reasonContent}>
                <Text style={styles.reasonText}>Supports All Graduates</Text>
                <Text style={styles.description}>Whether high school, college, or trade school, MyTassel is here for everyone.</Text>
              </View>
            </View>
            
            <View style={styles.reasonCard}>
              <Text style={styles.checkmark}>✅</Text>
              <View style={styles.reasonContent}>
                <Text style={styles.reasonText}>Gift Registry Integration</Text>
                <Text style={styles.description}>Easily include items to help you start your next chapter.</Text>
              </View>
            </View>
            
            <View style={styles.reasonCard}>
              <Text style={styles.checkmark}>✅</Text>
              <View style={styles.reasonContent}>
                <Text style={styles.reasonText}>Environmentally Friendly</Text>
                <Text style={styles.description}>Digital announcements mean less waste and more sustainability.</Text>
              </View>
            </View>
            
            <View style={styles.reasonCard}>
              <Text style={styles.checkmark}>✅</Text>
              <View style={styles.reasonContent}>
                <Text style={styles.reasonText}>Celebrate Your Way</Text>
                <Text style={styles.description}>MyTassel empowers you to make your moment truly your own.</Text>
              </View>
            </View>
          </View>
          
          <View style={styles.reasonCard}>
            <Text style={styles.checkmark}>✅</Text>
            <View style={styles.reasonContent}>
              <Text style={styles.reasonText}>Still Not Convinced and Want to Order Paper Announcements?</Text>
              <Text style={styles.description}>Create your MyTassel page and include your personal QR code to direct friends and family to your digital announcement, registry, and cash goal.</Text>
            </View>
          </View>
          
          <Text style={styles.footer}>Start your journey today with MyTassel—Your Moment, Your Way!</Text>
        </View>
        </ScrollView>
      </ImageBackground>
    </View>
  );
}

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    width: "100%",
    maxWidth: "100vw",
    overflowX: "hidden",
    position: "relative",
    alignItems: "center",
    minHeight: "100vh",
  },
  backgroundImage: {
    flex: 1,
    resizeMode: "cover",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100%",
    minHeight: "100vh",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    zIndex: 0,
  },
  body: {
    flex: 1,
    height: "auto",
  },
  scrollView: {
    flex: 1,
    position: 'relative',
  },
  backgroundTint: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.12)',
    zIndex: 1,
    pointerEvents: 'none',
  },
  backgroundImageBlur: {
    filter: 'blur(10px)',
    WebkitFilter: 'blur(10px)',
  },
  content: {
    padding: isMobile ? 16 : isTablet ? 32 : 48,
    zIndex: 2,
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
    position: 'relative',
  },
  title: {
    fontSize: isMobile ? 28 : isTablet ? 36 : 44,
    fontWeight: '600',
    marginBottom: 40,
    color: '#4c956c',
    textAlign: 'center',
    zIndex: 2,
    fontFamily: 'Poppins-SemiBold',
    color: '#fefde2',
  },
  reasonsGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : '1fr 1fr 1fr',
    gap: isMobile ? 16 : 24,
    zIndex: 2
  },
  reasonCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 12,
    padding: 24,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s ease',
    zIndex: 2,
    marginBottom: isMobile ? 16 : 24,
    backgroundColor: '#fefde2',
  },
  reasonContent: {
    flex: 1,
    gap: 8,
  },
  checkmark: {
    fontSize: 20,
    marginTop: 2,
  },
  reasonText: {
    fontSize: isMobile ? 18 : 20,
    fontWeight: '600',
    color: '#4c956c',
    fontFamily: 'Poppins-SemiBold',
  },
  description: {
    fontSize: isMobile ? 15 : 16,
    color: '#4A5568',
    fontFamily: 'Poppins-Regular',
  },
  footer: {
    fontSize: isMobile ? 18 : 20,
    fontWeight: '600',
    color: '#4c956c',
    textAlign: 'center',
    marginTop: 40,
    marginBottom: 20,
    zIndex: 2,
    fontFamily: 'Poppins-SemiBold',
    color: '#fefde2',
  },
  reasonImageContainer: {
    alignItems: 'center',
    marginTop: 15,
    width: '100%',
    zIndex: 2,
  },
  whyMyTasselImage: {
    width: '100%',
    height: isMobile ? 150 : isTablet ? 200 : 250,
    borderRadius: 8,
    marginTop: 10,
  },
  imageContainer: {
    alignItems: 'center',
    marginTop: 30,
    marginBottom: 20,
    width: '100%',
    zIndex: 2,
  },
  videoWrapper: {
    width: '100%',
    maxWidth: 900,
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: isMobile ? 16 : 32,
    marginBottom: isMobile ? 32 : 48,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoPlaceholder: {
    width: '100%',
    maxWidth: 900,
    aspectRatio: isSmallDesktop ? 16 / 9 : 16 / 9,
    minHeight: 200,
    maxHeight: 500,
    backgroundColor: '#000',
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoPlaceholderText: {
    color: '#fff',
    fontSize: isMobile ? 16 : 18,
    fontWeight: '600',
  },
  sampleButton: {
    width: 200,
    height: 44,
    backgroundColor: '#4c956c',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    shadowColor: '#000',
    shadowOpacity: 0.16,
    shadowOffset: { width: 0, height: 3 },
    shadowRadius: 6,
    marginBottom: 20,
  },
  sampleButtonText: {
    color: '#fefde2',
    fontSize: 18,
    fontWeight: '500',
  },
});

export default WhyMyTassel; 